// Account-wide reference lists shared by several slices' forms — see lib/api/CLAUDE.md.
import { baseApi } from './baseApi';
import { TAG_TYPES, siteMaterialsTag } from './tags';
import { handledError } from './listQuery';
import {
  buildLookupParams,
  processMaterials,
  processSiteMaterialIds,
  processSuppliers,
  processCustomers,
  processShippingDetails,
  processIsccCertificate,
  processTransportationRoutes,
  processMbPeriodOptions,
} from '@/lib/mappers';

const PATHS = {
  materials: '/materials',
  suppliers: '/suppliers',
  customers: '/customers',
  shippingDetails: (customerId) => `/customers/${customerId}/shipping-details`,
  isccCertificate: (number) => `/iscc-certificates/${encodeURIComponent(number)}`,
  supplierTransportation: (supplierId) => `/suppliers/${supplierId}/transportation`,
  siteMbPeriods: (siteId) => `/mb-periods/site/${siteId}`,
  siteMaterials: (siteId) => `/sites/materials/${siteId}`,
};

// TODO: server-side search once an account can outgrow one page.
const LOOKUP_LIMIT = 1000;

export const lookupsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // Scoped to the active module, server-side.
    getMaterials: build.query({
      query: (certificateType) => ({
        url: PATHS.materials,
        params: buildLookupParams({ limit: LOOKUP_LIMIT, certificateType }),
      }),
      transformResponse: processMaterials,
      providesTags: [{ type: TAG_TYPES.Material, id: 'LIST' }],
    }),

    getSuppliers: build.query({
      query: () => ({
        url: PATHS.suppliers,
        params: buildLookupParams({ limit: LOOKUP_LIMIT }),
      }),
      transformResponse: processSuppliers,
      providesTags: [{ type: TAG_TYPES.Supplier, id: 'LIST' }],
    }),

    getCustomers: build.query({
      query: () => ({
        url: PATHS.customers,
        params: buildLookupParams({ limit: LOOKUP_LIMIT }),
      }),
      transformResponse: processCustomers,
      providesTags: [{ type: TAG_TYPES.Customer, id: 'LIST' }],
    }),

    /**
     * A live, read-only lookup against the public ISCC directory — nothing is persisted,
     * so it carries no tag. `handledError`: a 404 means "no such certificate", which the
     * field renders itself; the error middleware must not toast it on every keystroke.
     */
    // Which materials a site's terminal is assigned (Material settings) — the form's material list narrows to it.
    getTerminalMaterialIds: build.query({
      query: ({ siteId, terminalId }) => ({
        url: PATHS.siteMaterials(siteId),
        params: { terminalId },
      }),
      transformResponse: processSiteMaterialIds,
      providesTags: (result, error, { siteId }) => [
        siteMaterialsTag(siteId),
        { type: TAG_TYPES.Material, id: 'LIST' },
      ],
    }),

    // The mass-balance periods a Summary report can be run for.
    getSiteMbPeriodOptions: build.query({
      query: ({ siteId, terminalId }) => ({
        url: PATHS.siteMbPeriods(siteId),
        ...(terminalId ? { params: { terminalId } } : {}),
      }),
      transformResponse: (dto) => processMbPeriodOptions(dto?.output?.data),
      transformErrorResponse: handledError,
      providesTags: (result, error, arg) => [
        { type: TAG_TYPES.Site, id: `MB_PERIOD-${arg.siteId}` },
      ],
    }),

    getIsccCertificate: build.query({
      query: (number) => ({ url: PATHS.isccCertificate(number) }),
      transformResponse: processIsccCertificate,
      transformErrorResponse: handledError,
    }),

    // One supplier's transport routes; each names its origin, destination and emissions.
    getSupplierTransportationRoutes: build.query({
      query: (supplierId) => ({ url: PATHS.supplierTransportation(supplierId) }),
      transformResponse: processTransportationRoutes,
      providesTags: (result, error, supplierId) => [
        { type: TAG_TYPES.Supplier, id: `TRANSPORT-${supplierId}` },
      ],
    }),

    // One customer's delivery sites — pass skipToken until "Sold to" is chosen.
    getCustomerShippingDetails: build.query({
      query: (customerId) => ({ url: PATHS.shippingDetails(customerId) }),
      transformResponse: processShippingDetails,
      providesTags: (result, error, customerId) => [
        { type: TAG_TYPES.Customer, id: `SHIPPING-${customerId}` },
      ],
    }),
  }),
});

export const {
  useGetMaterialsQuery,
  useGetTerminalMaterialIdsQuery,
  useGetSiteMbPeriodOptionsQuery,
  useGetSuppliersQuery,
  useGetCustomersQuery,
  useGetCustomerShippingDetailsQuery,
  useGetSupplierTransportationRoutesQuery,
  useGetIsccCertificateQuery,
} = lookupsApi;
